import React, { useEffect, useMemo, useState } from 'react';
import { CalendarBlank, FlowArrow, Question, WarningCircle } from '@phosphor-icons/react';
import type { Ticket } from '../types';
import { ladeVorgangsarten, type Vorgangsart } from '../../../../lib/vorgaenge/vorgangsarten';
import { fehlendeAngaben, fortschritt, fristLage, gruppiereVorgaenge, naechsterPunkt, type FristLage } from '../../../../lib/vorgaenge/uebersicht';

type VorgangsTicket = Ticket & { vorgangsart_id?: string | null; vorgang_angaben?: unknown };

const FRIST: Record<Exclude<FristLage, null>, { text: (d: string) => string; farbe: string; flaeche: string }> = {
  ueberfaellig: { text: d => `Überfällig · ${d}`, farbe: 'var(--bad)', flaeche: 'var(--bad-soft)' },
  heute: { text: () => 'Heute fällig', farbe: 'var(--warn)', flaeche: 'var(--warn-soft)' },
  bald: { text: d => `Fällig ${d}`, farbe: 'var(--warn)', flaeche: 'var(--warn-soft)' },
  spaeter: { text: d => `Fällig ${d}`, farbe: 'var(--ink2)', flaeche: 'var(--fill2)' },
};

const deDatum = (iso: string) => { const [j, m, t] = iso.slice(0, 10).split('-'); return `${t}.${m}.${j?.slice(2)}`; };
const heuteIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

/**
 * #489 Etappe V4: Ansicht „Vorgänge“ der Ticketliste. Bekommt die gefilterte Ticketliste,
 * zeigt nur offene Tickets mit Vorgangsart – je Vorgangsart gruppiert, mit Fortschritt der
 * Checkliste, nächstem Schritt, fehlenden Angaben und Frist.
 */
export default function VorgaengeAnsicht({ tickets, onOeffnen }: { tickets: Ticket[]; onOeffnen: (ticket: Ticket) => void }) {
  const [arten, setArten] = useState<Vorgangsart[]>([]);
  useEffect(() => { ladeVorgangsarten().then(setArten).catch(e => console.warn('Vorgangsarten laden fehlgeschlagen', e)); }, []);
  const heute = heuteIso();
  const gruppen = useMemo(() => gruppiereVorgaenge(tickets as VorgangsTicket[], arten, heute), [tickets, arten, heute]);

  return (
    <section aria-label="Vorgänge" className="flex flex-col" style={{ gap: 16 }}>
      <p className="m-0" style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--ink2)' }}>
        Vorgänge sind Tickets mit Vorgangsart (z. B. Kündigung, Schaden, Zählerstand). Sie entstehen in der Mail über „Vorgang starten“.
        Fortschritt = abgehakte Punkte der Checkliste; fehlende Angaben werden aus Antworten des Absenders automatisch nachgetragen.
      </p>

      {gruppen.length === 0 && (
        <div className="immo-glas flex flex-col items-center text-center" style={{ borderRadius: 24, padding: '36px 20px', gap: 8 }}>
          <FlowArrow size={30} weight="duotone" color="var(--acc)" />
          <span style={{ fontSize: 16, fontWeight: 800 }}>Keine offenen Vorgänge</span>
          <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Für die aktuelle Auswahl gibt es keine laufenden Vorgänge.</span>
        </div>
      )}

      {gruppen.map(g => (
        <div key={g.artId} className="flex flex-col" style={{ gap: 10 }}>
          <div className="flex items-baseline flex-wrap" style={{ gap: 10 }}>
            <span style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-0.01em' }}>{g.name}</span>
            <span className="immo-zahl" style={{ fontSize: 13, color: 'var(--ink3)' }}>{g.offen} offen</span>
            {g.ueberfaellig > 0 && <span className="immo-zahl" style={{ fontSize: 13, fontWeight: 700, color: 'var(--bad)' }}>{g.ueberfaellig} überfällig</span>}
          </div>
          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 12 }}>
            {g.tickets.map(t => {
              const f = fortschritt(t.beschreibung);
              const naechster = naechsterPunkt(t.beschreibung);
              const fehlt = fehlendeAngaben(t.vorgang_angaben);
              const lage = fristLage(t.faellig_am, heute);
              const frist = lage && t.faellig_am ? FRIST[lage] : null;
              const wer = t.mieter_rel ? `${t.mieter_rel.vorname ?? ''} ${t.mieter_rel.nachname ?? ''}`.trim() : '';
              const wo = t.objekt ? [t.objekt.strasse, t.objekt.hausnummer].filter(Boolean).join(' ') || t.objekt.bezeichnung : '';
              return (
                <button key={t.id} type="button" onClick={() => onOeffnen(t)} className="immo-glas immo-hover-widget flex flex-col text-left cursor-pointer"
                  style={{ borderRadius: 22, padding: 16, gap: 10, border: 'none', color: 'var(--ink)' }}>
                  <span className="flex items-start" style={{ gap: 8 }}>
                    <span className="flex flex-col flex-1 min-w-0">
                      <span className="truncate" style={{ fontSize: 14, fontWeight: 800 }}>{t.titel}</span>
                      <span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)' }}>#{t.ticket_nummer}{wer ? ` · ${wer}` : ''}{wo ? ` · ${wo}` : ''}</span>
                    </span>
                    {frist && (
                      <span className="flex items-center whitespace-nowrap shrink-0" style={{ height: 22, padding: '0 8px', borderRadius: 11, gap: 4, fontSize: 11, fontWeight: 800, background: frist.flaeche, color: frist.farbe }}>
                        {lage === 'ueberfaellig' ? <WarningCircle size={12} weight="fill" /> : <CalendarBlank size={12} weight="bold" />}{frist.text(deDatum(t.faellig_am!))}
                      </span>
                    )}
                  </span>
                  {f.gesamt > 0 && (
                    <span className="flex flex-col" style={{ gap: 4 }}>
                      <span className="flex justify-between" style={{ fontSize: 11, color: 'var(--ink3)' }}>
                        <span>Checkliste</span><span className="immo-zahl">{f.erledigt}/{f.gesamt}</span>
                      </span>
                      <span aria-hidden style={{ height: 6, borderRadius: 3, background: 'var(--fill2)', overflow: 'hidden' }}>
                        <span style={{ display: 'block', height: '100%', width: `${f.prozent}%`, borderRadius: 3, background: f.prozent === 100 ? 'var(--ok)' : 'var(--acc)' }} />
                      </span>
                    </span>
                  )}
                  {naechster && <span style={{ fontSize: 12, color: 'var(--ink2)' }}><b>Nächster Schritt:</b> {naechster}</span>}
                  {fehlt.length > 0 && (
                    <span className="flex flex-wrap items-center" style={{ gap: 4 }}>
                      <Question size={13} weight="fill" color="var(--warn)" />
                      {fehlt.map(x => (
                        <span key={x} style={{ height: 20, padding: '0 7px', borderRadius: 10, display: 'inline-flex', alignItems: 'center', fontSize: 11, fontWeight: 700, background: 'var(--warn-soft)', color: 'var(--warn)' }}>{x}</span>
                      ))}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </section>
  );
}
